"use client";

import React, { useEffect, useState } from "react";
import { useUseCelo } from "../providers/UseCeloProvider";
import { CheckoutButton } from "./CheckoutButton";
import { useCheckoutFlow } from "../hooks/useCheckoutFlow";
import { usePricingTableConfig } from "../hooks/usePricingTableConfig";
import * as LucideIcons from "lucide-react";

// Auth-agnostic PricingTable - no longer has built-in Clerk integration
// Users can pass auth state via externalAuth prop or use hardcodedUserId for testing

interface PricingConfig {
  model?: string;
  amount?: number;
  currency?: string;
  interval?: string;
  usage?: {
    unit?: string;
    tiers?: Array<{
      upTo: number;
      price: number;
    }>;
  };
}

interface Feature {
  id: string;
  name: string;
  description: string | null;
  config: any;
}

interface PlanFeature {
  feature: Feature;
  config: any;
}

interface Plan {
  id: string;
  name: string;
  description: string | null;
  pricing: any;
  cta: string | null;
  trialPeriodDays: number | null;
  features: PlanFeature[];
}

export interface PricingTableProps {
  // Legacy props (optional when using UseCeloProvider)
  appId?: string;
  publicKey?: string;
  apiHost?: string;

  // Component props
  upgradePageUrl?: string;
  onSelectPlan?: (planId: string) => void;
  className?: string;
  highlightPlanId?: string;
  showAnnualToggle?: boolean;

  // New checkout integration props
  useCheckoutButton?: boolean;
  successUrl?: string;
  cancelUrl?: string;
  openInNewTab?: boolean;
  requireConfirmation?: boolean;
  confirmationMessage?: string;
  onCheckoutSuccess?: (sessionUrl: string) => void;
  onCheckoutError?: (error: Error, planId: string) => void;
  // Enhanced checkout flow options
  enableDirectCheckout?: boolean;
  onAuthRequired?: (planId: string) => void | Promise<void>;
  authRedirectUrl?: string;
  testMode?: boolean;
  allowPromotionCodes?: boolean;
  useDefaultPages?: boolean;
  // For testing: hardcode a userId to bypass auth
  hardcodedUserId?: string;
  // External auth state (instead of Clerk integration)
  externalAuth?: {
    isAuthenticated: boolean;
    user: {
      id: string;
      email?: string;
      name?: string;
    } | null;
  };
  // Remote configuration
  useRemoteConfig?: boolean;
}

export function PricingTable({
  appId: propAppId,
  publicKey: propPublicKey,
  apiHost: propApiHost,
  upgradePageUrl,
  onSelectPlan,
  className = "",
  highlightPlanId,
  showAnnualToggle = false,
  useCheckoutButton = false,
  successUrl,
  cancelUrl,
  openInNewTab = false,
  requireConfirmation = false,
  confirmationMessage = "Proceed to checkout?",
  onCheckoutSuccess,
  onCheckoutError,
  // New checkout flow props
  enableDirectCheckout = false,
  onAuthRequired,
  authRedirectUrl,
  testMode = false,
  allowPromotionCodes = true,
  useDefaultPages = false,
  hardcodedUserId,
  externalAuth,
  useRemoteConfig = false,
}: PricingTableProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");

  // Try to get config from context, fallback to props
  let contextConfig = null;

  // Try to use the UseCelo provider context
  try {
    contextConfig = useUseCelo();
  } catch {
    // Not within provider, use props
  }

  // Determine final config (props override context for backward compatibility)
  const appId = propAppId || contextConfig?.appId;
  const publicKey = propPublicKey || contextConfig?.publicKey;
  const apiHost = propApiHost || contextConfig?.apiHost || "http://localhost:3000";

  // Fetch remote configuration if enabled
  const { config: remoteConfig, loading: configLoading } = usePricingTableConfig({
    appId: useRemoteConfig ? (appId || "") : "",
    apiUrl: apiHost,
  });

  // Auth is now handled via externalAuth prop - no built-in Clerk integration

  // Initialize checkout flow if direct checkout is enabled
  const checkoutFlow = enableDirectCheckout ? useCheckoutFlow({
    successUrl,
    cancelUrl,
    onAuthRequired,
    authRedirectUrl,
    allowPromotionCodes,
    useDefaultPages,
    testMode,
    hardcodedUserId, // Pass hardcoded userId for testing
    // Pass external auth if provided
    externalAuth,
    onCheckoutStarted: (planId) => {
      console.log(`🛒 PricingTable: Starting checkout for plan: ${planId}`);
    },
    onCheckoutError: (errorMessage, planId) => {
      console.error(`❌ PricingTable: Checkout error for plan ${planId}:`, errorMessage);
      if (onCheckoutError) {
        onCheckoutError(new Error(errorMessage), planId);
      }
    }
  }) : null;

  // Use external auth state if provided, otherwise no auth
  const finalAuthState = {
    isAuthenticated: externalAuth?.isAuthenticated || false,
    user: externalAuth?.user || null
  };

  // Extract styling from remote config (if enabled and loaded)
  const primaryColor = useRemoteConfig && remoteConfig?.colorScheme?.primary || undefined;
  const accentColor = useRemoteConfig && remoteConfig?.colorScheme?.accent || undefined;
  const textColor = useRemoteConfig && remoteConfig?.colorScheme?.text || undefined;
  const cardPadding = useRemoteConfig && remoteConfig?.spacing?.cardPadding || undefined;
  const borderRadius = useRemoteConfig && remoteConfig?.spacing?.borderRadius || undefined;
  const shadow = useRemoteConfig && remoteConfig?.shadow || undefined;
  const configuredPlanOrder = useRemoteConfig && remoteConfig?.planOrder || undefined;
  const configuredHighlightPlanId = useRemoteConfig && remoteConfig?.highlightedPlanId || highlightPlanId;

  console.log('🎨 PricingTable Remote Config:', {
    useRemoteConfig,
    configLoading,
    hasRemoteConfig: !!remoteConfig,
    primaryColor,
    accentColor,
    cardPadding,
    borderRadius,
    configuredPlanOrder,
    configuredHighlightPlanId
  });

  console.log('🔍 PricingTable render:', {
    enableDirectCheckout,
    hasCheckoutFlow: !!checkoutFlow,
    externalAuth: {
      isAuthenticated: externalAuth?.isAuthenticated || false,
      userId: externalAuth?.user?.id,
      email: externalAuth?.user?.email
    },
    // SDK auth removed - using simplified userId approach
    finalAuth: {
      isAuthenticated: finalAuthState.isAuthenticated,
      userId: finalAuthState.user?.id
    },
    isReady: checkoutFlow?.isReady,
    hardcodedUserId
  });

  // Validate required config
  if (!appId || !publicKey) {
    console.error(
      "PricingTable: Missing required config. Either provide appId and publicKey as props, " +
      "or wrap your app with UseCeloProvider."
    );
  }

  useEffect(() => {
    async function fetchPricingTablePlans() {
      // Skip if missing required config
      if (!appId || !publicKey) {
        setError("Missing required configuration");
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const res = await fetch(`${apiHost}/api/public/plans/${appId}`, {
          headers: {
            "x-api-key": publicKey,
          },
        });
        
        if (!res.ok) {
          throw new Error("Failed to fetch plans");
        }
        
        const { data } = await res.json();
        setPlans(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load pricing");
      } finally {
        setLoading(false);
      }
    }

    fetchPricingTablePlans();
  }, [appId, publicKey, apiHost]);

  const getPricingDisplay = (pricing: any): { amount: string; interval: string; model: string } => {
    if (!pricing || typeof pricing !== "object") {
      return { amount: "Free", interval: "", model: "free" };
    }

    const pricingConfig = pricing as any;
    const currency = pricingConfig.currency || "USD";
    const currencySymbol = currency === "USD" ? "$" : currency;
    
    // Check for free plan
    if (pricingConfig.model === "free") {
      return { amount: "Free", interval: "", model: "free" };
    }
    
    // Usage-based pricing
    if (pricingConfig.model === "usage" || pricingConfig.model === "usage_based") {
      const basePrice = pricingConfig.usage?.tiers?.[0]?.price || pricingConfig.baseFee || 0;
      return { 
        amount: basePrice === 0 ? "Free" : `Starting at ${currencySymbol}${basePrice}`,
        interval: basePrice === 0 ? "" : `per ${pricingConfig.usage?.unit || "unit"}`,
        model: "usage"
      };
    }
    
    // Get the price amount - handle both 'amount' and 'baseFee' fields
    const amount = pricingConfig.amount ?? pricingConfig.baseFee ?? pricingConfig.price ?? 0;
    
    // If amount is 0, it's free
    if (amount === 0) {
      return { amount: "Free", interval: "", model: "free" };
    }
    
    // Get the billing interval - handle both 'interval' and 'billingCycle' fields
    const interval = pricingConfig.interval || pricingConfig.billingCycle || "month";
    
    // Calculate display amount based on billing cycle
    const displayAmount = billingCycle === "annual" && showAnnualToggle 
      ? Math.floor(amount * 12 * 0.8) // 20% discount for annual
      : amount;
    
    const displayInterval = billingCycle === "annual" && showAnnualToggle
      ? "year"
      : interval;
    
    return {
      amount: `${currencySymbol}${displayAmount}`,
      interval: `/${displayInterval}`,
      model: pricingConfig.model || "flat"
    };
  };

  const getFeatureDisplay = (planFeature: PlanFeature) => {
    const config = planFeature.config || {};
    const feature = planFeature.feature;
    
    // If config has specific limits, display them
    if (config.rateLimit) {
      return `${config.rateLimit.toLocaleString()} ${feature.name}/month`;
    }
    if (config.maxUsers) {
      return `Up to ${config.maxUsers} users`;
    }
    if (config.maxStorageGB) {
      return `${config.maxStorageGB} GB storage`;
    }
    if (config.maxItems) {
      return `${config.maxItems} ${feature.name}`;
    }
    if (config.retentionDays) {
      return `${config.retentionDays} days data retention`;
    }
    if (config.enabled === false) {
      return null; // Don't show disabled features
    }
    
    // Default display
    return feature.name;
  };

  const handleSelectPlan = async (planId: string) => {
    console.log('🎯 PricingTable: handleSelectPlan called with planId:', planId);
    console.log('🔍 PricingTable: enableDirectCheckout:', enableDirectCheckout);
    console.log('🔍 PricingTable: finalAuthState:', finalAuthState);
    console.log('🔍 PricingTable: checkoutFlow exists:', !!checkoutFlow);

    // If enableDirectCheckout is true, use direct checkout with external auth
    if (enableDirectCheckout && checkoutFlow) {
      console.log('✅ PricingTable: Using direct checkout flow');
      console.log('🔍 Auth check - isAuthenticated:', finalAuthState.isAuthenticated);
      console.log('🔍 Auth check - user:', finalAuthState.user);

      // Always use checkoutFlow.startCheckout which now has external auth override
      console.log('🚀 PricingTable: Starting checkout with external auth override');
      try {
        await checkoutFlow.startCheckout(planId);
        console.log('✅ PricingTable: Checkout flow completed');
      } catch (error) {
        console.error('❌ PricingTable: Checkout flow error:', error);
      }
      return;
    }

    console.log('⚠️ PricingTable: Using fallback behavior');
    // Otherwise, use the original behavior
    if (onSelectPlan) {
      console.log('🔄 PricingTable: Calling onSelectPlan');
      onSelectPlan(planId);
    } else if (upgradePageUrl) {
      console.log('🔄 PricingTable: Redirecting to upgradePageUrl');
      window.location.href = `${upgradePageUrl}?planId=${planId}`;
    } else {
      console.log('❌ PricingTable: No action configured for plan selection');
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading pricing...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <div className="text-center text-red-600">
          <p>Failed to load pricing</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <div className="text-center text-muted-foreground">
          <p>No pricing plans available</p>
        </div>
      </div>
    );
  }

  // Calculate orderedPlans for grid layout
  const orderedPlansForGrid = configuredPlanOrder
    ? configuredPlanOrder.map(id => plans.find(p => p.id === id)).filter((p): p is Plan => p !== undefined)
    : plans;

  return (
    <div className={`w-full ${className}`}>
      {/* Annual/Monthly Toggle */}
      {showAnnualToggle && (
        <div className="flex items-center justify-center mb-8 gap-4">
          <span className={billingCycle === "monthly" ? "font-semibold" : "text-muted-foreground"}>
            Monthly
          </span>
          <button
            onClick={() => setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly")}
            className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                billingCycle === "annual" ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
          <span className={billingCycle === "annual" ? "font-semibold" : "text-muted-foreground"}>
            Annual
            {showAnnualToggle && (
              <span className="ml-2 text-green-600 text-sm font-medium">Save 20%</span>
            )}
          </span>
        </div>
      )}

      {/* Pricing Cards */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 16px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: useRemoteConfig && remoteConfig?.columnsPerRow
            ? `repeat(${Math.min(remoteConfig.columnsPerRow, orderedPlansForGrid.length)}, 1fr)`
            : orderedPlansForGrid.length === 1 ? '1fr'
            : orderedPlansForGrid.length === 2 ? 'repeat(2, 1fr)'
            : 'repeat(auto-fit, minmax(300px, 400px))',
          gap: '24px',
          justifyContent: 'center',
          width: '100%',
          maxWidth: useRemoteConfig && remoteConfig?.columnsPerRow
            ? `${remoteConfig.columnsPerRow * 420}px`
            : undefined
        }}>
        {(() => {
          // Apply plan ordering from remote config if available
          // Defensive: show all plans, even if some aren't in configuredPlanOrder (newly added plans)
          const orderedPlans = configuredPlanOrder
            ? [
                // First: plans in the configured order
                ...configuredPlanOrder.map(id => plans.find(p => p.id === id)).filter((p): p is Plan => p !== undefined),
                // Then: any plans NOT in the configured order (newly added plans)
                ...plans.filter(p => !configuredPlanOrder.includes(p.id))
              ]
            : plans;

          return orderedPlans.map((plan, index) => {
          const pricing = getPricingDisplay(plan.pricing);
          const isHighlighted = plan.id === configuredHighlightPlanId || (!configuredHighlightPlanId && index === 1);

          // When using remote config, match the preview pane structure exactly
          if (useRemoteConfig && remoteConfig) {
            const sectionOrder = remoteConfig.sectionOrder || ["title", "description", "price", "cta", "features"];

            // Get feature order for this plan
            const featureOrderForPlan = remoteConfig.featureOrder?.[plan.id] ||
              (plan.features || []).map((_: any, idx: number) => idx.toString());

            // Map feature indices, but ensure we show ALL features even if not in saved order
            const savedOrderedFeatures = featureOrderForPlan
              .map((idx: string) => plan.features?.[parseInt(idx)])
              .filter(Boolean);

            // Find any features not in the saved order (newly added features)
            const featuresNotInOrder = (plan.features || []).filter((_: any, idx: number) =>
              !featureOrderForPlan.includes(idx.toString())
            );

            // Combine ordered features with newly added ones - show all features
            const orderedFeatures = [...savedOrderedFeatures, ...featuresNotInOrder];

            const sections: Record<string, React.ReactNode> = {
              title: (
                <h3
                  key="title"
                  style={{
                    fontSize: '24px',
                    fontWeight: remoteConfig.typography?.fontWeight || 'bold',
                    fontFamily: remoteConfig.typography?.fontFamily || 'inherit',
                    color: textColor || '#111827',
                    marginBottom: '8px'
                  }}
                >
                  {plan.name}
                </h3>
              ),
              description: remoteConfig.showDescription && plan.description ? (
                <p key="description" style={{ color: textColor || '#4B5563', opacity: 0.7, fontSize: '14px', marginBottom: '16px' }}>
                  {plan.description}
                </p>
              ) : null,
              price: (
                <div key="price" style={{ marginBottom: '24px' }}>
                  <span style={{ fontSize: '36px', fontWeight: 'bold', color: textColor || '#111827' }}>{pricing.amount}</span>
                  <span style={{ color: textColor || '#4B5563', opacity: 0.7, marginLeft: '4px' }}>{pricing.interval}</span>
                </div>
              ),
              cta: remoteConfig.showCTA ? (
                <button
                  key="cta"
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={checkoutFlow?.isProcessing}
                  data-plan-id={plan.id}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    fontWeight: '600',
                    color: 'white',
                    transition: 'all 0.3s',
                    margin: '24px 0',
                    backgroundColor: primaryColor,
                    borderRadius: borderRadius || "8px",
                    border: 'none',
                    cursor: checkoutFlow?.isProcessing ? 'not-allowed' : 'pointer',
                    opacity: checkoutFlow?.isProcessing ? 0.6 : 1
                  }}
                >
                  {checkoutFlow?.isProcessing ? "Processing..." : (plan.cta || "Get Started")}
                </button>
              ) : null,
              features: remoteConfig.showFeatures && orderedFeatures.length > 0 ? (
                <ul key="features" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {orderedFeatures.map((pf: any, idx: number) => {
                    const featureIcon = pf.feature.icon;
                    const isEmoji = featureIcon && /\p{Emoji}/u.test(featureIcon);
                    const IconComponent = featureIcon && !isEmoji ? (LucideIcons as any)[featureIcon] : null;

                    return (
                      <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14px', color: textColor || '#374151' }}>
                        {IconComponent ? (
                          <IconComponent
                            style={{
                              width: '20px',
                              height: '20px',
                              flexShrink: 0,
                              marginTop: '2px',
                              color: accentColor || '#10B981'
                            }}
                          />
                        ) : featureIcon && isEmoji ? (
                          <span style={{
                            width: '20px',
                            height: '20px',
                            flexShrink: 0,
                            marginTop: '2px',
                            fontSize: '16px',
                            lineHeight: '1'
                          }}>
                            {featureIcon}
                          </span>
                        ) : (
                          <svg
                            style={{
                              width: '20px',
                              height: '20px',
                              flexShrink: 0,
                              marginTop: '2px',
                              color: accentColor || '#10B981'
                            }}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                        <span>{pf.feature.name}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : null,
            };

            return (
              <div
                key={plan.id}
                className={`relative overflow-hidden transition-all ${
                  isHighlighted ? "ring-2 ring-offset-2" : ""
                }`}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: remoteConfig.colorScheme?.background || 'white',
                  borderRadius: borderRadius || '8px',
                  padding: cardPadding || '24px',
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderColor: isHighlighted ? accentColor : "#E5E7EB",
                  boxShadow: shadow || undefined,
                  width: '100%',
                  maxWidth: '400px',
                }}
              >
                {/* Highlighted Badge */}
                {isHighlighted && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      padding: '4px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'white',
                      backgroundColor: accentColor || '#10B981',
                    }}
                  >
                    Recommended
                  </div>
                )}

                {/* Render sections in configured order */}
                {sectionOrder.map((sectionKey) => sections[sectionKey]).filter(Boolean)}
              </div>
            );
          }

          // Default rendering (original PricingTable structure)
          return (
            <div
              key={plan.id}
              className={`relative border-2 shadow-sm transition-all hover:shadow-lg ${
                !primaryColor ? `rounded-2xl p-6 ${isHighlighted ? "border-primary bg-primary/5 scale-105" : "border-gray-200"}` : ''
              }`}
              style={primaryColor ? {
                borderRadius: borderRadius || '16px',
                padding: cardPadding || '24px',
                borderColor: '#E5E7EB',
                backgroundColor: 'white',
                boxShadow: shadow || undefined,
              } : undefined}
            >
              {/* Popular Badge */}
              {isHighlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span
                    className={`text-white text-xs font-semibold px-3 py-1 rounded-full ${primaryColor ? '' : 'bg-primary'}`}
                    style={primaryColor ? { backgroundColor: primaryColor } : {}}
                  >
                    Most Popular
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                {plan.description && (
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                )}
              </div>

              {/* Pricing Display */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold">{pricing.amount}</span>
                  {pricing.interval && (
                    <span className="text-muted-foreground">{pricing.interval}</span>
                  )}
                </div>
                {plan.trialPeriodDays && (
                  <p className="text-sm text-green-600 mt-2">
                    {plan.trialPeriodDays} day free trial
                  </p>
                )}
              </div>

              {/* Features List */}
              <ul className="space-y-3 mb-6">
                {plan.features?.map((planFeature) => {
                  const display = getFeatureDisplay(planFeature);
                  if (!display) return null;
                  
                  return (
                    <li key={planFeature.feature.id} className="flex items-start gap-2">
                      <svg
                        className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm">{display}</span>
                    </li>
                  );
                })}
              </ul>

              {/* CTA Button */}
              {useCheckoutButton ? (
                <CheckoutButton
                  planId={plan.id}
                  successUrl={successUrl}
                  cancelUrl={cancelUrl}
                  openInNewTab={openInNewTab}
                  requireConfirmation={requireConfirmation}
                  confirmationMessage={confirmationMessage}
                  onSuccess={onCheckoutSuccess}
                  onCheckoutError={(error) => onCheckoutError?.(error, plan.id)}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                    isHighlighted
                      ? "bg-primary text-white hover:bg-primary/90"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                  }`}
                >
                  {plan.cta || "Get Started"}
                </CheckoutButton>
              ) : (
                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={checkoutFlow?.isProcessing}
                  data-plan-id={plan.id}
                  className={`w-full py-3 px-4 font-semibold transition-all ${
                    checkoutFlow?.isProcessing
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed rounded-lg"
                      : !primaryColor ? `rounded-lg ${isHighlighted ? "bg-primary text-white hover:bg-primary/90" : "bg-gray-100 text-gray-900 hover:bg-gray-200"}` : ''
                  }`}
                  style={!checkoutFlow?.isProcessing && primaryColor ? {
                    backgroundColor: primaryColor,
                    color: 'white',
                    borderRadius: borderRadius || '8px'
                  } : undefined}
                >
                  {checkoutFlow?.isProcessing ? "Processing..." : (plan.cta || "Get Started")}
                </button>
              )}
            </div>
          );
        })})()}
        </div>
      </div>
    </div>
  );
}